import { useEffect, useMemo, useState } from "react";
import { ListItemIcon, ListItemText, Menu, MenuItem } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import GroupAddOutlinedIcon from "@mui/icons-material/GroupAddOutlined";
import BedtimeIcon from "@mui/icons-material/Bedtime";
import CheckIcon from "@mui/icons-material/Check";
import LockIcon from "@mui/icons-material/Lock";
import QuestionMarkIcon from "@mui/icons-material/QuestionMark";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";
import Card from "../../../components/ui/Card";
import { HatimIcon, MuzakereIcon } from "../../../components/ui/mdiIcons";
import { useScriptMode } from "../../../contexts/ScriptModeContext";
import { useGetMyKazaQuery, useGetMyWeekMuzakeresQuery } from "../../../api/personalApi";
import { useGetMyHatimInstancesQuery } from "../../../api/hatimApi";
import { hatimDayProgress } from "../../../lib/meter";
import { remainingLabel } from "../../../lib/hatimEditing";
import { cls } from "../../hatim/soft/SoftKit";
import { prayersFor, showKazaCircle, sumKaza } from "../../../lib/kaza";
import {
  memberRowState,
  muzakereCircleState,
  sortWeekMuzakeres,
  todayString,
  weekRange,
} from "../../../lib/muzakereWeek";
import { formatClock } from "../../../lib/focusTimer";
import useFocusTimer from "../../focus/useFocusTimer";
import FocusDialog from "../../focus/FocusDialog";
import MuzakereDialog from "./MuzakereDialog";
import HatimDialog, { coversDay } from "./HatimDialog";
import JoinHatimDrawer from "./JoinHatimDrawer";
import KazaDrawer from "../../kaza/KazaDrawer";
import JoinGroupModal from "../../groups/components/JoinGroupModal";

const COLORS = {
  deep: "#006A71",
  brand: "#48A6A7",
  neutral: "#8A9BA0",
  success: "#2E9E6A",
  warning: "#E8A23A",
};

function Badge({ color, children, wide }) {
  return (
    <span
      className={`absolute -top-0.5 -right-0.5 h-[18px] ${wide ? "min-w-[18px] px-1" : "w-[18px]"} rounded-full text-white text-[10px] leading-none flex items-center justify-center ring-2 ring-white dark:ring-[#123B3D]`}
      style={{ backgroundColor: color }}
    >
      {children}
    </span>
  );
}

/** Yuvarlağın çevresindeki ilerleme halkası (hatim payının okunma oranı). */
function Ring({ progress }) {
  const r = 35;
  const c = 2 * Math.PI * r;
  return (
    <svg className="absolute -inset-[6px] -rotate-90 pointer-events-none" width={76} height={76} aria-hidden>
      <circle cx={38} cy={38} r={r} fill="none" strokeWidth={3} className="stroke-terakkiBrandMist dark:stroke-white/15" />
      <circle
        cx={38}
        cy={38}
        r={r}
        fill="none"
        strokeWidth={3}
        strokeLinecap="round"
        className="stroke-terakkiPrimaryDark dark:stroke-white"
        strokeDasharray={c}
        strokeDashoffset={c * (1 - Math.min(1, Math.max(0, progress)))}
      />
    </svg>
  );
}

/** Şeritteki tek yuvarlak: içinde simge ve kısa ad, altında başlık. */
function Circle({ Icon, inner, label, sublabel, sublabelTone, fill, innerClass, faded, badge, progress, onClick, testId }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      data-circle={testId}
      className={`group w-[88px] sm:w-[100px] shrink-0 flex flex-col items-center gap-2 rounded-xl pt-2.5 pb-1.5 ${
        onClick ? "hover:bg-terakkiBrandCloud dark:hover:bg-white/[0.06] transition-colors" : ""
      }`}
    >
      <span className="relative">
        {progress != null ? <Ring progress={progress} /> : null}
        <span
          className={`w-[64px] h-[64px] rounded-full flex flex-col items-center justify-center gap-0.5 shadow-sm transition-transform duration-200 ${
            onClick ? "group-hover:-translate-y-0.5 group-hover:shadow-md group-active:scale-95" : ""
          } ${innerClass || "text-white"} ${faded ? "opacity-40" : ""}`}
          style={fill ? { backgroundColor: fill } : undefined}
        >
          <Icon sx={{ fontSize: 22 }} aria-hidden />
          <span className="text-[11.5px] leading-tight">{inner}</span>
        </span>
        {badge}
      </span>
      <span className="w-full flex flex-col items-center">
        <span className={`w-full text-base text-center truncate ${cls.title}`} title={label}>
          {label}
        </span>
        {sublabel ? (
          <span className={`w-full text-sm text-center truncate ${sublabelTone || cls.muted}`}>{sublabel}</span>
        ) : null}
      </span>
    </Tag>
  );
}

/** Sıralı belirme: yuvarlaklar soldan sağa, kısa gecikmelerle gelir. */
function CircleSlot({ index, children }) {
  return (
    <div className="shrink-0 motion-safe:animate-pop-in" style={{ animationDelay: `${Math.min(index, 10) * 45}ms` }}>
      {children}
    </div>
  );
}

function dayDiff(date, today) {
  return Math.round((new Date(`${date}T00:00:00`) - new Date(`${today}T00:00:00`)) / 86400000);
}

/**
 * Terakkim'de başlığın altındaki daire şeridi (mobil TerakkimCircles):
 * en başta tek "Katıl" yuvarlağı (hatime ya da gruba katılma), ardından
 * seçili günün hatimleri, haftanın müzakereleri ve kaza. Yapılmamışlar
 * başta, en yakın olan en solda; tamamlananlar sona kayar.
 * Sayaç burada yaşar: pencere kapansa da seans sürer, düğmede süre görünür.
 */
export default function DayCirclesCard({ localDate, monthKey, onChanged, onHatimProgressChange }) {
  const { t } = useScriptMode();
  const { start, end } = weekRange(localDate);
  const today = todayString();

  // Yeni gün/hafta/ay için veri gelene kadar iskelet (currentData yok + çekiliyor);
  // sekmeye dönüşteki arka plan tazelemesi iskelet göstermez.
  const weekQ = useGetMyWeekMuzakeresQuery({ startDate: start, endDate: end });
  const kazaQ = useGetMyKazaQuery({ localDate });
  const hatimQ = useGetMyHatimInstancesQuery({ monthKey });
  const pending = (q) => q.isLoading || (q.isFetching && !q.currentData);
  const weekResp = weekQ.currentData;
  const kazaResp = kazaQ.currentData;
  const hatimResp = hatimQ.currentData;
  const weekLoading = pending(weekQ);
  const kazaLoading = pending(kazaQ);
  const hatimLoading = pending(hatimQ);
  const items = useMemo(() => sortWeekMuzakeres(weekResp?.data?.muzakeres || [], today), [weekResp, today]);
  const hatims = useMemo(
    () => (hatimResp?.data?.instances || []).filter((i) => coversDay(i, localDate)),
    [hatimResp, localDate]
  );
  const kaza = kazaResp?.data || null;

  const [selectedId, setSelectedId] = useState(null);
  const [hatimId, setHatimId] = useState(null);
  // Kaza çekmecesi: null kapalı, 0 gün sekmesi, 1 kaza borcu sekmesi.
  const [kazaTab, setKazaTab] = useState(null);
  const [focusOpen, setFocusOpen] = useState(false);
  const [joinAnchor, setJoinAnchor] = useState(null);
  const [joinHatimOpen, setJoinHatimOpen] = useState(false);
  const [joinGroupOpen, setJoinGroupOpen] = useState(false);
  const timer = useFocusTimer();
  const selected = items.find((m) => m.id === selectedId) || null;
  const selectedHatim = hatims.find((h) => h.id === hatimId) || null;

  // Günün metresi: hatim payının okunma oranı.
  const hatimStep = useMemo(() => hatimDayProgress(hatims), [hatims]);
  useEffect(() => {
    if (onHatimProgressChange && !hatimLoading) onHatimProgressChange(hatimStep);
  }, [hatimStep, hatimLoading, onHatimProgressChange]);

  // Sekme başlığında süre: arka plandaki sekmede de sayaç görünsün.
  const clock = timer.session
    ? formatClock(timer.session.targetSeconds > 0 ? timer.remaining : timer.elapsed)
    : null;
  useEffect(() => {
    if (!clock) return undefined;
    const previous = document.title;
    document.title = `${clock} · ${t("focus_title")}`;
    return () => {
      document.title = previous;
    };
  }, [clock, t]);

  const kazaPrayers = kaza ? kaza.prayers || prayersFor(kaza.mezhep) : [];
  const kazaRemaining = kaza ? sumKaza(kaza.remaining, kazaPrayers) : 0;
  const kazaToday = kaza ? sumKaza(kaza.day, kazaPrayers) : 0;
  const showKaza = showKazaCircle(kaza);
  const loading = weekLoading || kazaLoading || hatimLoading;
  // Mobildeki gibi: geçmiş haftada ve ileri günde hatime katılım sunulmaz.
  const canJoinHatim = localDate >= weekRange(today).start && localDate <= today;

  const circles = [];

  hatims.forEach((inst) => {
    const ratio = hatimDayProgress([inst]);
    const done = Boolean(inst.isCompleted) || (ratio != null && ratio >= 1);
    const remaining = remainingLabel(inst.endDate, t);
    const endMs = inst.endDate ? new Date(inst.endDate).getTime() - Date.now() : Number.MAX_SAFE_INTEGER;
    circles.push({
      key: `hatim-${inst.id}`,
      done,
      near: endMs / 86400000,
      node: (
        <Circle
          key={`hatim-${inst.id}`}
          testId={`hatim-${inst.id}`}
          Icon={HatimIcon}
          inner={t("circles_hatim")}
          label={inst.name}
          sublabel={remaining?.text}
          sublabelTone={remaining?.tone}
          fill={done ? COLORS.brand : COLORS.deep}
          progress={ratio ?? 0}
          onClick={() => setHatimId(inst.id)}
          badge={
            done ? (
              <Badge color={COLORS.success}>
                <CheckIcon sx={{ fontSize: 12 }} />
              </Badge>
            ) : null
          }
        />
      ),
    });
  });

  if (hatims.length === 0 && !canJoinHatim) {
    circles.push({
      key: "hatim-empty",
      done: true,
      near: Number.MAX_SAFE_INTEGER,
      node: (
        <Circle
          key="hatim-empty"
          testId="hatim-empty"
          Icon={HatimIcon}
          inner={t("circles_hatim")}
          label={t("circles_no_hatim")}
          fill={COLORS.neutral}
          faded
        />
      ),
    });
  }

  if (items.length === 0) {
    circles.push({
      key: "muzakere-empty",
      done: true,
      near: Number.MAX_SAFE_INTEGER,
      node: (
        <Circle
          key="muzakere-empty"
          testId="muzakere-empty"
          Icon={MuzakereIcon}
          inner={t("circles_muzakere")}
          label={t("circles_no_muzakere")}
          fill={COLORS.neutral}
          faded
        />
      ),
    });
  }

  items.forEach((item) => {
    const row = memberRowState(item.assessment);
    const state = muzakereCircleState(item, today);
    let badge = null;
    if (item.done) {
      badge = (
        <Badge color={COLORS.success}>
          <CheckIcon sx={{ fontSize: 12 }} />
        </Badge>
      );
    } else if (row === "answer") {
      badge = (
        <Badge color={COLORS.warning}>
          <QuestionMarkIcon sx={{ fontSize: 11 }} />
        </Badge>
      );
    } else if (row === "notOpen" || (row === "plain" && state === "upcoming")) {
      badge = (
        <Badge color={COLORS.neutral}>
          <LockIcon sx={{ fontSize: 10 }} />
        </Badge>
      );
    } else if (row === "plain" && item.date === today) {
      badge = <Badge color={COLORS.warning} />;
    }
    // Bugün en yakın, geçmiş günler onu izler, ileri tarihliler en sonda.
    const diff = dayDiff(item.date, today);
    circles.push({
      key: `muzakere-${item.id}`,
      done: item.done,
      near: diff <= 0 ? -diff : 1000 + diff,
      node: (
        <Circle
          key={`muzakere-${item.id}`}
          testId={`muzakere-${item.id}`}
          Icon={MuzakereIcon}
          inner={t("circles_muzakere")}
          label={item.title}
          fill={item.done ? COLORS.brand : COLORS.deep}
          faded={!item.done && state !== "open"}
          onClick={() => setSelectedId(item.id)}
          badge={badge}
        />
      ),
    });
  });

  if (showKaza) {
    circles.push({
      key: "kaza",
      // Kaza her gün; o gün hiç kılınmadıysa yapılacaklar arasında.
      done: kazaToday > 0,
      near: 0,
      node: (
        <Circle
          key="kaza"
          testId="kaza"
          Icon={BedtimeIcon}
          inner={t("circles_kaza")}
          label={`${kazaRemaining.toLocaleString("tr-TR")} ${t("circles_left")}`}
          fill={kazaToday > 0 ? COLORS.brand : COLORS.deep}
          onClick={() => setKazaTab(0)}
          badge={
            kazaToday > 0 ? (
              <Badge color={COLORS.success} wide>
                {kazaToday}
              </Badge>
            ) : null
          }
        />
      ),
    });
  }

  if (!showKaza && !kazaLoading) {
    // Borç girilmemiş ya da bitmiş: soluk yuvarlak, kaza borcu sekmesini açar.
    circles.push({
      key: "kaza-setup",
      done: true,
      near: Number.MAX_SAFE_INTEGER - 1,
      node: (
        <Circle
          key="kaza-setup"
          testId="kaza-setup"
          Icon={BedtimeIcon}
          inner={t("circles_kaza")}
          label={t("circles_kaza_setup")}
          fill={COLORS.neutral}
          faded
          onClick={() => setKazaTab(1)}
        />
      ),
    });
  }

  circles.sort((a, b) => (a.done === b.done ? a.near - b.near : a.done ? 1 : -1));

  const chooseJoin = (open) => {
    setJoinAnchor(null);
    open(true);
  };

  return (
    <Card className="!p-0 overflow-hidden motion-safe:animate-rise-in [animation-delay:.06s]" data-tour="circles">
      <div className="flex gap-1 sm:gap-2 overflow-x-auto px-2 sm:px-3 py-3">
        {loading ? (
          [0, 1, 2, 3].map((i) => (
            <div key={i} className="w-[88px] sm:w-[100px] shrink-0 flex flex-col items-center gap-2 pt-2.5 pb-1.5">
              <span className={`w-[64px] h-[64px] rounded-full ${cls.skeleton}`} />
              <span className={`h-3 w-12 rounded ${cls.skeleton}`} />
            </div>
          ))
        ) : (
          <>
            {/* Hatime ve gruba katılma tek yuvarlakta, hep en başta. */}
            <CircleSlot index={0}>
            <Circle
              testId="join"
              Icon={AddIcon}
              inner={t("circles_join")}
              label={canJoinHatim ? `${t("circles_hatim")} · ${t("circles_group")}` : t("circles_group")}
              innerClass="bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-terakkiPrimaryDark dark:text-white"
              onClick={(e) => setJoinAnchor(e.currentTarget)}
            />
            </CircleSlot>
            {/* Odaklan: sayaç sürerken dolu, altında süre */}
            <CircleSlot index={1}>
              <Circle
                testId="focus"
                Icon={TimerOutlinedIcon}
                inner={t("circles_focus")}
                label={clock || t("focus_title")}
                fill={timer.session ? COLORS.deep : undefined}
                innerClass={
                  timer.session
                    ? "text-white"
                    : "bg-terakkiBrandCloud text-terakkiPrimaryDark dark:bg-terakkiPrimaryDark dark:text-white"
                }
                onClick={() => setFocusOpen(true)}
              />
            </CircleSlot>
            {circles.map((circle, i) => (
              <CircleSlot key={circle.key} index={i + 2}>
                {circle.node}
              </CircleSlot>
            ))}
          </>
        )}
      </div>

      <Menu
        anchorEl={joinAnchor}
        open={Boolean(joinAnchor)}
        onClose={() => setJoinAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        {canJoinHatim ? (
          <MenuItem onClick={() => chooseJoin(setJoinHatimOpen)} data-join="hatim">
            <ListItemIcon>
              <HatimIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText>{t("circles_join_hatim")}</ListItemText>
          </MenuItem>
        ) : null}
        <MenuItem onClick={() => chooseJoin(setJoinGroupOpen)} data-join="group">
          <ListItemIcon>
            <GroupAddOutlinedIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>{t("groups_join_cta")}</ListItemText>
        </MenuItem>
      </Menu>

      <KazaDrawer
        open={kazaTab !== null}
        localDate={localDate}
        initialTab={kazaTab ?? 0}
        onClose={() => setKazaTab(null)}
        onSaved={onChanged}
      />
      <MuzakereDialog item={selected} onClose={() => setSelectedId(null)} onChanged={onChanged} />
      <HatimDialog instance={selectedHatim} onClose={() => setHatimId(null)} />
      <JoinHatimDrawer open={joinHatimOpen} onClose={() => setJoinHatimOpen(false)} />
      <JoinGroupModal open={joinGroupOpen} onClose={() => setJoinGroupOpen(false)} onJoined={() => setJoinGroupOpen(false)} />
      <FocusDialog open={focusOpen} onClose={() => setFocusOpen(false)} timer={timer} onSaved={onChanged} localDate={localDate} />
    </Card>
  );
}
